import React, { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { Text, Tooltip } from 'components/ds'

import { PHONE_BREAKPOINT_PX } from './constants'

type Props = {
  children: string
}

/**
 * Text that truncates to a single line with an ellipsis, revealing the full
 * value in a tooltip only when it actually overflows. Below the `sm` breakpoint
 * it wraps instead — there is no hover to reveal a tooltip on touch, so the row
 * grows to fit the copy.
 */
const TruncatedText = ({ children }: Props) => {
  const shouldTruncate = useMediaQuery({ minWidth: PHONE_BREAKPOINT_PX })
  // Held in state, not a ref: wrapping in the tooltip remounts the element, and
  // the effect has to re-subscribe to the new node rather than keep measuring
  // the detached one.
  const [textNode, setTextNode] = useState<HTMLSpanElement | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    if (!textNode) return

    const measure = () =>
      setIsTruncated(textNode.scrollWidth > textNode.clientWidth)

    measure()

    // The column width tracks the modal, so re-measure on resize too.
    const observer = new ResizeObserver(measure)
    observer.observe(textNode)

    return () => observer.disconnect()
  }, [textNode, children, shouldTruncate])

  const text = (
    <Text ref={setTextNode} size="2" truncate={shouldTruncate}>
      {children}
    </Text>
  )

  return isTruncated ? <Tooltip content={children}>{text}</Tooltip> : text
}

export default React.memo<Props>(TruncatedText)
